import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  Signal,
  SimpleChanges,
  WritableSignal
} from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { Item } from "@interfaces/nfe-payload";
import { Option } from "@interfaces/option";
import { PNCompany } from "@interfaces/pn-company";
import { Store } from "@ngrx/store";
import { selectNcmsAsOptions } from "@state/selectors/user.selectors";
import { debounceTime, Subscription } from "rxjs";
import cfopList from "@static/cfop.json";

@Component({
  selector: "app-item-card",
  templateUrl: "./item-card.component.html",
  styleUrls: ["./item-card.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ItemCardComponent implements OnChanges {
  @Output() remove = new EventEmitter<string>();
  @Output() product = new EventEmitter<Item>();
  @Input() item?: WritableSignal<Item>;
  @Input() index = 0;
  @Input() company?: Signal<PNCompany | null>;

  userNcms = this.store.selectSignal(selectNcmsAsOptions);
  cfopList = cfopList;

  ncms = computed<Option[]>(() => [
    {
      display: "Cadastrar NCM",
      value: "NOVO_NCM"
    },
    ...this.userNcms()
  ]);

  unitOptions = [
    {
      display: "UN",
      value: "UN"
    },
    {
      display: "KG",
      value: "KG"
    },
    {
      display: "M²",
      value: "M²"
    },
    {
      display: "M",
      value: "M"
    },
    {
      display: "PC",
      value: "PC"
    },
    {
      display: "LT",
      value: "LT"
    },
    {
      display: "CX",
      value: "CX"
    }
  ];

  form = new FormGroup({
    codigo: new FormControl("", Validators.required),
    codigoBarras: new FormControl(""),
    codigoBarrasTributavel: new FormControl(""),
    descricao: new FormControl("", Validators.required),
    ncm: new FormControl("", Validators.required),
    cfop: new FormControl("", Validators.required),
    quantidade: new FormGroup({
      comercial: new FormControl("1", Validators.required),
      tributavel: new FormControl("1", Validators.required)
    }),
    valorUnitario: new FormGroup({
      comercial: new FormControl("0", Validators.required),
      tributavel: new FormControl("0", Validators.required)
    }),
    unidade: new FormGroup({
      comercial: new FormControl("UN", Validators.required),
      tributavel: new FormControl("UN", Validators.required)
    }),
    informacoesComplementares: new FormControl(""),
    valorFrete: new FormControl("0"),
    valorSeguro: new FormControl("0"),
    valorBaseCalculoIcms: new FormControl<number | null>(null)
  });

  $: Subscription[] = [];

  get valorUnitario() {
    return this.form.controls.valorUnitario;
  }

  get quantidade() {
    return this.form.controls.quantidade;
  }

  get unidade() {
    return this.form.controls.unidade;
  }

  constructor(
    private store: Store,
    private destroyRef: DestroyRef,
    private router: Router
  ) {
    this.$.push(
      this.valorUnitario.controls.comercial.valueChanges.subscribe((value) => {
        this.valorUnitario.controls.tributavel.setValue(value);
      }),
      this.form.controls.codigoBarras.valueChanges.subscribe((value) => {
        this.form.controls.codigoBarrasTributavel.setValue(value);
      }),
      this.quantidade.controls.comercial.valueChanges.subscribe((value) => {
        this.quantidade.controls.tributavel.setValue(value);
      }),
      this.unidade.controls.comercial.valueChanges.subscribe((value) => {
        this.unidade.controls.tributavel.setValue(value);
      }),
      this.form.valueChanges.pipe(debounceTime(200)).subscribe((product) => {
        const isLucroPresumidoOuReal = 
          this.company?.()?.regimeTributario === '3' || 
          this.company?.()?.regimeTributario === '4';
        
        this.item?.update((item) => ({
          ...item,
          codigo: product.codigo ?? "",
          codigoBarras: product.codigoBarras ?? "",
          codigoBarrasTributavel: product.codigoBarrasTributavel ?? "",
          descricao: product.descricao ?? "",
          informacoesComplementares: product.informacoesComplementares ?? "",
          ncm: product.ncm ?? "",
          cfop: product.cfop ?? "",
          quantidade: {
            comercial: product.quantidade?.comercial ?? "",
            tributavel: product.quantidade?.tributavel ?? ""
          },
          valorUnitario: {
            comercial: product.valorUnitario?.comercial ?? "",
            tributavel: product.valorUnitario?.tributavel ?? ""
          },
          unidade: {
            comercial: product.unidade?.comercial ?? "",
            tributavel: product.unidade?.tributavel ?? ""
          },
          ...(isLucroPresumidoOuReal && product.valorBaseCalculoIcms !== null && {
            valorBaseCalculoIcms: Number(product.valorBaseCalculoIcms) || undefined
          })
        }));
      })
    );

    this.destroyRef.onDestroy(() => {
      this.$.forEach(($) => $.unsubscribe());
    });
  }

  handleNcmChange(value: string) {
    if (value === "NOVO_NCM") {
      this.router.navigate(["/settings"], {
        queryParams: {
          tab: 1
        }
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["item"]) {
      const item = this.item?.();
      this.form.controls.codigo.setValue(item?.codigo ?? "");
      this.form.controls.descricao.setValue(item?.descricao ?? "");
      this.quantidade.controls.comercial.setValue(
        item?.quantidade.comercial.toString() ?? "1"
      );
      this.valorUnitario.controls.comercial.setValue(
        item?.valorUnitario.comercial.toString() ?? ""
      );
      this.form.controls.ncm.setValue(item?.ncm ?? "");
      this.form.controls.cfop.setValue(item?.cfop ?? "");
      this.form.controls.valorBaseCalculoIcms.setValue(
        item?.valorBaseCalculoIcms ?? null
      );
    }

    // Aplica validação condicional quando company mudar
    if (changes["company"] || changes["item"]) {
      this.applyBaseCalculoValidation();
    }
  }

  /**
   * Aplica validação condicional no campo "Valor da BC" para LP/LR
   */
  private applyBaseCalculoValidation() {
    const regimeTributario = this.company?.()?.regimeTributario;
    const isLucroPresumidoOuReal = regimeTributario === '3' || regimeTributario === '4';
    
    const valorBcControl = this.form.controls.valorBaseCalculoIcms;
    
    if (isLucroPresumidoOuReal) {
      valorBcControl.setValidators([Validators.required]);
    } else {
      valorBcControl.clearValidators();
    }
    
    valorBcControl.updateValueAndValidity({ emitEvent: false });
  }
}
